import { getAnalytics, isSupported, logEvent, type Analytics } from 'firebase/analytics';
import { getFirebaseApp } from './firebase';
import { measurementId } from './env';

/**
 * GA4 for the Studio funnel.
 *
 * NO PII GOES TO GA4. Not the address, email, phone, name, postal code, or the
 * lat/lng the gate resolves. Google's terms prohibit it and it would put customer
 * PII in a third-party store the privacy policy does not cover. Inherited as
 * policy from dac-qualifier, where it was spec rule D22; Studio's own spec has no
 * numbered equivalent yet, so this comment is the statement of the rule rather
 * than a pointer to one.
 *
 * ALLOWLIST, not a denylist, and that inversion is the whole design. A denylist
 * can only reject keys that announce themselves (`email`, `phone`, `zip`); it
 * cannot catch a key carrying PII silently. `lat`/`lng` are far finer geography
 * than the ZIP this bans, and a freeform `q` or `addr` is exactly what a caller
 * reaches for when spreading a state object into an event. So nothing reaches GA4
 * unless its key is named below.
 *
 * `county` is allowed because 62 counties is coarse geography, not identification,
 * and it is the one field the service-area gate already reports. `status` is the
 * `EstimateResponse` arm, `reason` the `DeclineReason` -- both are closed enums
 * defined in `@ks-studio/shared`, so neither can carry freeform text.
 *
 * SILENT STRIP: an unlisted key vanishes from the event with no error. If a
 * parameter is missing from GA4 DebugView, check the list before losing an
 * afternoon. (The old denylist had the same trap in reverse: `countyName`
 * disappeared because `name` is a substring.)
 *
 * Never throws, never blocks the UI, and no-ops outside a production build. The
 * `import.meta.env.PROD` check in `initAnalytics`, not the presence of a
 * measurement id, is what keeps dev and PR previews out of the GA4 property.
 */

/**
 * The only params a caller may pass to `track()`. `context` is deliberately
 * excluded from this set: it is computed below and merged into the payload
 * after stripping, so a caller can never override the real render context by
 * spreading a same-named key into `params`.
 */
const CALLER_ALLOWED_KEYS: ReadonlySet<string> = new Set([
  'step',
  'submission_id',
  'county',
  // `DeclineReason` from the gate: out_of_state | out_of_area | location_unknown.
  'reason',
  // `EstimateResponse.status`: ok | solar_api_miss.
  'status',
]);

/*
 * DELIBERATELY NOT HERE YET: panelsCount, arrayKwDc, annualAcKwh.
 *
 * They are roof facts rather than PII and would pass the rule above, but Task 22
 * owns coverage measurement and has not chosen its numerator yet. Adding keys
 * speculatively is what turns an allowlist back into a denylist, so the decision
 * is left where it belongs.
 *
 * Dropped from dac-qualifier's list: `is_dac`, which Studio has no concept of,
 * and `channel`, which was DAC's tel-or-email contact choice. Studio's contact
 * step collects both, so there is no choice to record.
 */

const context: 'embed' | 'standalone' =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('embed')
    ? 'embed'
    : 'standalone';

type Primitive = string | number | boolean;
type EventParams = Record<string, Primitive>;

type Logger = (event: string, params: Record<string, unknown>) => void;

let testLogger: Logger | undefined;
/** Test seam. Replaces the GA4 sink so events can be asserted. Never called in production. */
export function __setLoggerForTests(logger: Logger | undefined): void {
  testLogger = logger;
}

let initPromise: Promise<Analytics | null> | null = null;

function initAnalytics(): Promise<Analytics | null> {
  initPromise ??= (async () => {
    if (!import.meta.env.PROD || !measurementId()) return null;
    try {
      if (!(await isSupported())) return null;
      const app = getFirebaseApp();
      return app ? getAnalytics(app) : null;
    } catch {
      return null;
    }
  })();
  return initPromise;
}

function strip(params: EventParams): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (CALLER_ALLOWED_KEYS.has(key)) out[key] = value;
  }
  return out;
}

/** Fire and forget. Always tagged with the render context. */
export function track(event: string, params: EventParams = {}): void {
  const payload = { context, ...strip(params) };

  if (testLogger) {
    try {
      testLogger(event, payload);
    } catch {
      /* analytics must never break the funnel */
    }
    return;
  }

  void initAnalytics()
    .then((analytics) => {
      if (analytics) logEvent(analytics, event, payload);
    })
    .catch(() => {
      /* analytics must never break the funnel */
    });
}
